import React, { useState, useEffect } from 'react';
import {
    Dialog, DialogTitle, DialogContent, DialogActions, Button, Typography, IconButton,
} from '@material-ui/core';
import { makeStyles } from '@material-ui/core/styles';
import CloseIcon from '@material-ui/icons/Close';
import PropTypes from 'prop-types';
import ArrowForwardIcon from '@material-ui/icons/ArrowForward';
import ArrowBackIcon from '@material-ui/icons/ArrowBack';
import TuneIcon from '@material-ui/icons/Tune';
import { useMutation } from '@apollo/client';
import AppReleaseMutate from 'services/graphQL/mutate/core/AppReleaseMutate';
import moment from 'moment';
import marked from 'marked';
import { Link as RouterLink } from 'react-router-dom';
import AppReleaseStyles from 'styles/modules/settings/AppReleaseStyles';

const useStyles = makeStyles((theme) => {
    const shared = AppReleaseStyles.shared(theme);
    const { colors } = shared;
    return {
        dialog: {
            borderRadius: 24,
            height: 550,
            maxHeight: 550,
            display: 'flex',
            flexDirection: 'column',
        },
        title: {
            fontWeight: 800,
            color: colors.slate[800],
            fontSize: '1.5rem',
            lineHeight: 1.2,
            paddingRight: theme.spacing(4),
        },
        subHeader: {
            color: colors.slate[500],
            fontSize: '0.9rem',
            display: 'flex',
            alignItems: 'center',
            gap: theme.spacing(1),
            marginTop: theme.spacing(0.5),
        },
        typeTag: shared.typeTag,
        typeFeature: shared.typeFeature,
        typeFix: shared.typeFix,
        typeImprovement: shared.typeImprovement,
        typeOther: shared.typeOther,

        content: {
            ...shared.content,
            flex: 1,
            overflowY: 'auto',
            padding: theme.spacing(0, 4, 3, 4),
        },
        historyLink: {
            display: 'flex',
            alignItems: 'center',
            gap: theme.spacing(1),
            color: colors.blue[600],
            textDecoration: 'none',
            fontSize: '0.85rem',
            fontWeight: 600,
            marginTop: theme.spacing(3),
            padding: theme.spacing(1, 0),
            borderTop: `1px solid ${colors.slate[100]}`,
            '&:hover': {
                textDecoration: 'underline',
            },
        },
        footer: {
            padding: theme.spacing(2, 3),
            borderTop: `1px solid ${colors.slate[100]}`,
            display: 'grid',
            gridTemplateColumns: '1fr auto 1fr',
            alignItems: 'center',
        },
        dotsContainer: {
            display: 'flex',
            gap: theme.spacing(1),
        },
        dot: {
            width: 8,
            height: 8,
            borderRadius: '50%',
            backgroundColor: colors.slate[200],
            transition: 'all 0.3s ease',
        },
        activeDot: {
            backgroundColor: colors.blue[600],
            transform: 'scale(1.2)',
        },
        prevButton: {
            justifySelf: 'start',
            borderRadius: 8,
            padding: '8px 24px',
            textTransform: 'none',
            fontWeight: 700,
            boxShadow: 'none',
            '&:hover': {
                boxShadow: `0 4px 12px ${colors.blue[600]}33`,
            },
            '&.Mui-disabled': {
                backgroundColor: 'transparent',
                boxShadow: 'none',
                color: colors.slate[400],
            },
        },
        nextButton: {
            justifySelf: 'end',
            borderRadius: 8,
            padding: '8px 24px',
            textTransform: 'none',
            fontWeight: 700,
            boxShadow: 'none',
            '&:hover': {
                boxShadow: `0 4px 12px ${colors.blue[600]}33`, // 33 is 20% opacity in hex
            },
        },
        closeButton: shared.closeButton,
    };
});

const AppReleaseDialog = ({
    open, releases = [], onClose, onMarkAsRead,
}) => {
    const classes = useStyles();
    const [currentIndex, setCurrentIndex] = useState(0);
    const [internalReleases, setInternalReleases] = useState([]);
    const [markAllAsRead, { loading: markingAsRead }] = useMutation(AppReleaseMutate.MARK_ALL_APP_RELEASES_AS_READ);

    useEffect(() => {
        if (open && internalReleases.length === 0 && releases.length > 0) {
            setInternalReleases(releases);
        }
    }, [open, releases, internalReleases.length]);

    useEffect(() => {
        if (!open) {
            setInternalReleases([]);
            setCurrentIndex(0);
        }
    }, [open]);

    if (!open || internalReleases.length === 0) return null;

    const currentRelease = internalReleases[currentIndex];

    if (!currentRelease) {
        return null;
    }

    const handleNext = () => {
        if (currentIndex < internalReleases.length - 1) {
            setCurrentIndex(currentIndex + 1);
        }
    };

    const handleBack = () => {
        if (currentIndex > 0) {
            setCurrentIndex(currentIndex - 1);
        }
    };

    const handleGotIt = async () => {
        await markAllAsRead();
        onClose();
        if (onMarkAsRead) onMarkAsRead();
    };

    const getTypeClass = (type) => {
        switch (type?.toLowerCase()) {
        case 'feature': return classes.typeFeature;
        case 'fix': return classes.typeFix;
        case 'improvement': return classes.typeImprovement;
        default: return classes.typeOther;
        }
    };

    return (
        <Dialog
            open={open}
            onClose={onClose}
            maxWidth="sm"
            fullWidth
            PaperProps={{ className: classes.dialog }}
        >
            <IconButton className={classes.closeButton} onClick={onClose}>
                <CloseIcon />
            </IconButton>

            <DialogTitle>
                <Typography className={classes.title}>
                    {currentRelease?.title}
                </Typography>
                <div className={classes.subHeader}>
                    <Typography variant="inherit">
                        {moment(currentRelease?.releaseDate).format('MMMM D, YYYY')}
                    </Typography>
                    <span className={`${classes.typeTag} ${getTypeClass(currentRelease?.type)}`}>
                        {currentRelease?.type}
                    </span>
                </div>
            </DialogTitle>

            <DialogContent>
                <div
                    className={classes.content}
                    // eslint-disable-next-line react/no-danger
                    dangerouslySetInnerHTML={{ __html: marked(currentRelease.content) }}
                />

                <RouterLink to="/release-notes" className={classes.historyLink} onClick={onClose}>
                    <TuneIcon fontSize="small" />
                    View full update history in Release Notes page
                </RouterLink>
            </DialogContent>

            <DialogActions className={classes.footer}>
                <Button
                    className={classes.prevButton}
                    onClick={handleBack}
                    disabled={currentIndex === 0}
                    variant={currentIndex === 0 ? 'text' : 'contained'}
                    color={currentIndex === 0 ? 'default' : 'primary'}
                    startIcon={currentIndex > 0 ? <ArrowBackIcon /> : null}
                    disableRipple
                >
                    Previous
                </Button>

                <div className={classes.dotsContainer}>
                    {internalReleases.map((_, idx) => (
                        <div
                            key={idx}
                            className={`${classes.dot} ${idx === currentIndex ? classes.activeDot : ''}`}
                        />
                    ))}
                </div>

                {currentIndex < internalReleases.length - 1 ? (
                    <Button
                        variant="contained"
                        color="primary"
                        className={classes.nextButton}
                        endIcon={<ArrowForwardIcon />}
                        onClick={handleNext}
                    >
                        Next
                    </Button>
                ) : (
                    <Button
                        variant="contained"
                        color="primary"
                        className={classes.nextButton}
                        onClick={handleGotIt}
                        disabled={markingAsRead}
                    >
                        Got it!
                    </Button>
                )}
            </DialogActions>
        </Dialog>
    );
};

AppReleaseDialog.propTypes = {
    open: PropTypes.bool.isRequired,
    releases: PropTypes.arrayOf(PropTypes.object),
    onClose: PropTypes.func.isRequired,
    onMarkAsRead: PropTypes.func,
};

AppReleaseDialog.defaultProps = {
    releases: [],
    onMarkAsRead: null,
};

export default AppReleaseDialog;
